import ax from "axios";
import axios from "@/axios/defaultAxios";
import { slices } from "@/constants/sliceNames";
import { createAsyncThunk } from "@reduxjs/toolkit";
import { BASE_S3_URL } from "@/constants/constantsStrings";
import { DatasetTypes } from "@/constants/dataset-types";
import { Dataset } from "@/entities/Dataset";
import { ExecutionStatuses } from "@/constants/execution-statuses";
import { TaskTypes } from "@/constants/task-types";
import { getDataset, getDatasetsList } from "@/datasources/datasets/api";

import { errorMessage, successMessage } from "../actions/messages.actions";

const ENDPOINT = "/datasets";

/******* Get Datasets ********/

export const requestMyDatasets = createAsyncThunk(
  `${slices.datasets}/requestMyDatasets`,
  async ({ params, resolve, actionOptions }, { rejectWithValue, dispatch }) => {
    try {
      const response = await getDatasetsList(params);
      if (response.status === 200 || response.status === 201) {
        resolve && resolve(response.data.data.data);
        return {
          datasets: response.data.data.data,
          records_count: response.data.data.records_count,
          actionOptions,
        };
      } else throw new Error(response);
    } catch (error) {
      dispatch(errorMessage(error.response.data.message));
      return rejectWithValue(error);
    }
  }
);

export const requestMyDatasetsStatus = {
  [requestMyDatasets.fulfilled]: (state, action) => {
    state.loading = false;
    state.datasets = action.payload.datasets;
    state.records_count = action.payload.records_count;
    state.message = action.payload?.message;
  },
  [requestMyDatasets.pending]: (state, action) => {
    state.loading = true;
  },
  [requestMyDatasets.rejected]: (state, action) => {
    state.loading = false;
    state.message = action.payload?.message;
  },
};

/******* Delete Dataset ********/

export const requestDeleteDataset = createAsyncThunk(
  `${slices.datasets}/requestDeleteDataset`,
  async (dataID, { rejectWithValue, dispatch }) => {
    try {
      const response = await axios.delete(`${ENDPOINT}/${dataID}`);
      if (response.status === 200 || response.status === 201) {
        dispatch(successMessage(response.data.message));
        return {
          dataID,
          message: response.data.message,
        };
      } else throw new Error(response);
    } catch (error) {
      dispatch(errorMessage(error.response.data.error || error.message));
      return rejectWithValue(error.response.data.message);
    }
  }
);

export const requestDeleteDatasetStatus = {
  [requestDeleteDataset.fulfilled]: (state, action) => {
    state.datasets = state.datasets.filter((dataset) => dataset._id !== action.payload.dataID);
    state.message = action.payload?.message;
    state.loading = false;
  },
  [requestDeleteDataset.pending]: (state, action) => {
    state.loading = true;
  },
  [requestDeleteDataset.rejected]: (state, action) => {
    state.message = action.payload?.message;
    state.loading = false;
  },
};

/******* Add Dataset to favorites ********/

export const requestAddFavoriteDataset = createAsyncThunk(
  `${slices.datasets}/requestAddFavoriteDataset`,
  async ({ dataID, isFavorite }, { rejectWithValue, dispatch }) => {
    try {
      const flag = !isFavorite ? 1 : 0;
      const response = await axios.put(`data/${dataID}/fav?isFavorite=${flag}`);
      if (response.status === 200 || response.status === 201) {
        dispatch(successMessage(response.data.message));
        return {
          dataID,
          isFavorite,
          message: response.data.message,
        };
      } else throw new Error(response);
    } catch (error) {
      dispatch(errorMessage(error.response.data.error || error.message));
      return rejectWithValue(error.response.data);
    }
  }
);

export const requestAddFavoriteDatasetStatus = {
  [requestAddFavoriteDataset.fulfilled]: (state, action) => {
    state.datasets = state.datasets.map((dataset) => {
      if (dataset._id === action.payload.dataID) {
        dataset.favourite = action.payload.isFavorite;
      }
      return dataset;
    });
    state.message = action.payload?.message;
    state.loading = false;
  },
  [requestAddFavoriteDataset.pending]: (state, action) => {
    state.loading = true;
  },
  [requestAddFavoriteDataset.rejected]: (state, action) => {
    state.message = action.payload?.message;
    state.loading = false;
  },
};

/******* Get Dataset ********/

export async function getTabularInfo(prevURL, visURL) {
  let tabularInfo = {};
  tabularInfo.previewData = await ax.get(prevURL);
  tabularInfo.visualizeData = await ax.get(visURL);

  return tabularInfo;
}

export async function getImageInfo(prevURL, visURL) {
  let dataInfo = {};

  dataInfo.previewData = await ax.get(prevURL);
  dataInfo.visualizeData = await ax.get(visURL);

  return dataInfo;
}
export async function getQualityCheckInfo(prevURL, visURL) {
  let QSInfo = {};

  QSInfo.previewData = await ax.get(prevURL);
  QSInfo.visualizeData = await ax.get(visURL);

  return QSInfo;
}

export async function getUnsupervisedImageInfo(prevURL, visURL) {
  let USIInfo = {};

  USIInfo.previewData = await ax.get(prevURL);

  return USIInfo;
}

export const requestSingleDataset = createAsyncThunk(
  `${slices.datasets}/requestSingleDataset`,
  async (datasetId, { rejectWithValue, dispatch }) => {
    try {
      const response = await getDataset(datasetId);
      if (response.status === 200 || response.status === 201) {
        const dataset = new Dataset(response.data.data);
        if (dataset.type === DatasetTypes.DEFAULT) {
          dataset.analysis_status = ExecutionStatuses.SUCCEEDED;
        }

        if (!dataset.is_labeled) {
          dataset.analysis_status = ExecutionStatuses.SUCCEEDED;
          return dataset;
        }

        if (dataset.analysis_output?.preview_key === "" && dataset.analysis_output?.visualize_key === "")
          return dataset;

        if (dataset.analysis_status === ExecutionStatuses.SUCCEEDED) {
          const getCurrentActionData = dataset.getDataAction(dataset.type);
          const response = await getCurrentActionData(
            dataset.analysis_output.preview_key,
            dataset.analysis_output.visualize_key
          );
          dataset.addDataInfo(response);
        }
        return dataset;
      }
    } catch (error) {
      dispatch(errorMessage(error.response.data.error || error.message));
      return rejectWithValue(error.response.data);
    }
  }
);

export const requestSingleDatasetStatus = {
  [requestSingleDataset.fulfilled]: (state, action) => {
    let tasks = [TaskTypes.CLASSIFICATION];
    if (action.payload.analysis_output?.tasks) {
      tasks = [...action.payload.analysis_output?.tasks];
    }
    state.dataset = action.payload;
    state.dataset.datasetTasks = tasks;
    state.loading = false;
  },
  [requestSingleDataset.pending]: (state, action) => {
    state.loading = true;
  },
  [requestSingleDataset.rejected]: (state, action) => {
    state.loading = false;
  },
};

/******* Get Sensor ********/

export const requestSingleSensor = createAsyncThunk(
  `${slices.datasets}/requestSingleSensor`,
  async (url, { rejectWithValue, dispatch }) => {
    try {
      const response = await ax.get(url);
      return response.data;
    } catch (error) {
      dispatch(errorMessage(error.response.statusText || error.message));
      return rejectWithValue(error.response.data);
    }
  }
);

export const requestSingleSensorStatus = {
  [requestSingleSensor.fulfilled]: (state, action) => {
    state.dataset.singleSensor = action.payload;
    state.loading = false;
  },
  [requestSingleSensor.pending]: (state, action) => {
    state.loading = true;
  },
  [requestSingleSensor.rejected]: (state, action) => {
    state.loading = false;
  },
};

/******* Get Time data ********/

export const requestTimeData = createAsyncThunk(
  `${slices.datasets}/requestTimeData`,
  async (url, { rejectWithValue, dispatch }) => {
    try {
      const response = await ax.get(url);
      return { timeData: response.data };
    } catch (error) {
      dispatch(errorMessage(error.response.statusText || error.message));
      return rejectWithValue(error.response.data);
    }
  }
);

export const requestTimeDataStatus = {
  [requestTimeData.fulfilled]: (state, action) => {
    state.dataset.timeData = action.payload.timeData;
    state.loading = false;
  },
  [requestTimeData.pending]: (state, action) => {
    state.loading = true;
  },
  [requestTimeData.rejected]: (state, action) => {
    state.loading = false;
  },
};

/****** update a dataset ********/

export const requestUpdateDataset = createAsyncThunk(
  `${slices.datasets}/requestUpdateDataset`,
  async ({ datasetID, IsPublic, description, tags, license, name }, { rejectWithValue, dispatch }) => {
    try {
      let body = {
        is_public: IsPublic,
      };
      if (description != null) {
        body["description"] = description;
      }
      if (tags) {
        body["tags"] = tags;
      }
      if (license != null) {
        body["license"] = license;
      }
      body["name"] = name;
      const response = await axios.put(`${ENDPOINT}/${datasetID}`, body);
      if (response.status === 200 || response.status === 201) {
        dispatch(successMessage("Dataset updated successfully"));
        return { message: response.data.message, dataset: body };
      }
      throw new Error(response.data.error);
    } catch (error) {
      dispatch(errorMessage(error.response.statusText || error.message));
      return rejectWithValue(error.response.data || error.message);
    }
  }
);

export const requestUpdateDatasetStatus = {
  [requestUpdateDataset.fulfilled]: (state, action) => {
    state.loading = false;
    state.message = action.payload?.message;
    state.dataset.is_public = action.payload.dataset.is_public;
    state.dataset.description = action.payload.dataset.description;
    state.dataset.tags = action.payload.dataset.tags;
    state.dataset.license = action.payload.dataset.license;
  },
  [requestUpdateDataset.pending]: (state, action) => {
    state.loading = true;
  },
  [requestUpdateDataset.rejected]: (state, action) => {
    state.loading = false;
    state.message = action.payload;
  },
};

/******* Download Dataset ********/

export const downloadDataset = (datasetID) => {
  return (dispatch) => {
    return axios
      .get(`${ENDPOINT}/download?id=${datasetID}`)
      .then((response) => {
        if (response.status === 200 || response.status === 201) {
          window.open(response.data.data.s3_url, "_blank");
          return response;
        }
        throw new Error(response.data.error);
      })
      .catch((error) => {
        dispatch(errorMessage(error.response.data.error || error.message));
      });
  };
};

/******* Label Dataset ********/

export const requestLabelDataset = createAsyncThunk(
  `${slices.datasets}/requestLabelDataset`,
  async ({ data, resolve, reject }, { rejectWithValue, dispatch }) => {
    try {
      const response = await axios.post(`${ENDPOINT}/label/${data.dataset_id}`, data);
      dispatch(successMessage("Creating a dataset label completed successfully"));
      resolve && resolve(response.data.data.project_id);
      return response.data;
    } catch (error) {
      dispatch(errorMessage(error.response.data || "Creating labeling dataset failed"));
      reject && reject(error);
      return rejectWithValue(error.response.data || error.message);
    }
  }
);

export const requestLabelDatasetStatus = {
  [requestLabelDataset.fulfilled]: (state, action) => {
    state.loading = false;
  },
  [requestLabelDataset.pending]: (state, action) => {
    state.loading = true;
  },
  [requestLabelDataset.rejected]: (state, action) => {
    state.loading = false;
    state.message = action.payload;
  },
};
